import { useState, useEffect, useMemo } from 'react';
import { fetchUserTeamsForCompany } from '../services/userMembership';

/**
 * @param {string | undefined} userUID
 * @param {string | undefined} companyId
 * @param {number} [refreshKey] — bump after e.g. creating a team to refetch
 * @returns {{ teams: Array<{ teamId: string, teamName: string }>, teamOptions: Array<{ label: string, value: string }>, loading: boolean, error: string | null }}
 */
export function useUserTeamsForCompany(userUID, companyId, refreshKey = 0) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userUID || !companyId) {
      setTeams([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchUserTeamsForCompany(userUID, companyId)
      .then((res) => {
        if (cancelled) return;
        if (res.success) {
          setTeams(res.data);
        } else {
          setTeams([]);
          setError(res.error || 'Failed to load teams');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setTeams([]);
          setError(e?.message || 'Failed to load teams');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userUID, companyId, refreshKey]);

  const teamOptions = useMemo(
    () =>
      teams.map((t) => ({
        label: t.teamName,
        value: t.teamId,
      })),
    [teams]
  );

  return { teams, teamOptions, loading, error };
}
